export class PropertyMediaHandler {
    root: HTMLElement | Document;
    container: HTMLElement | null;
    targets: HTMLCollectionOf<Element>;
    btns: HTMLCollectionOf<Element>;
    active: string;
    boundUpdate: () => void;
    boundOnPropertyClose: () => void;

    constructor( container: HTMLElement | Document ) {
        this.root = container;
        this.container = this.getContainer();
        this.targets = this.getTargets();
        this.btns = this.getBtns();
        this.active = 'none';

        this.boundUpdate = this.update.bind( this );
        this.boundOnPropertyClose = this.onPropertyClose.bind( this );

        document.addEventListener( 'twc:popup:open-property', this.boundUpdate );
        document.addEventListener( 'twc:popup:close-property', this.boundOnPropertyClose );

        this.update();
    }

    onPropertyClose() {
        this.active = 'none';
    }

    update() {
        this.container = this.getContainer();
        this.targets = this.getTargets();
        this.btns = this.getBtns();

        for ( let i = 0; i < this.btns.length; i++ ) {
            const btn = this.btns[ i ] as HTMLElement;

            if ( btn.classList.contains( PropertyMediaHandler.classes.initialised ) ) {
                continue;
            }

            btn.addEventListener( 'click', this.onClick.bind( this ) );
            btn.classList.add( PropertyMediaHandler.classes.initialised );
        }

        for ( let targetIndex = 0; targetIndex < this.targets.length; targetIndex++ ) {
            const target = this.targets[ targetIndex ] as HTMLElement;

            if ( target.classList.contains( PropertyMediaHandler.classes.active ) ) {
                this.active = target.dataset.type || 'none';
            }
        }
    }

    onClick( event: MouseEvent ) {
        const target = event.target as HTMLElement;

        if ( !target || !target.dataset.mediaAction ) {
            return;
        }

        const targetType = target.dataset.mediaAction;

        if ( targetType === this.active ) {
            return;
        }

        target.classList.add( 'btn-media-launcher--active' );

        for ( let i = 0; i < this.btns.length; i++ ) {
            const btn = this.btns[ i ] as HTMLElement;

            if ( btn.dataset.mediaAction !== targetType ) {
                btn.classList.remove( 'btn-media-launcher--active' );
            }
        }

        if ( targetType === 'none' || targetType === 'ai-photos' ) {
            this.container?.classList.remove( 'twc__property--slider-hidden' );

            for ( let hideableIndex = 0; hideableIndex < this.targets.length; hideableIndex++ ) {
                const hideable = this.targets[ hideableIndex ] as HTMLElement;
                hideable.classList.remove( PropertyMediaHandler.classes.active );
            }

            if ( targetType === 'ai-photos' ) {
                target.dispatchEvent( new CustomEvent( 'twc:ai-photos:activate', { bubbles: true } ) );
            } else {
                target.dispatchEvent( new CustomEvent( 'twc:ai-photos:deactivate', { bubbles: true } ) );
            }
        } else {
            target.dispatchEvent( new CustomEvent( 'twc:ai-photos:deactivate', { bubbles: true } ) );

            this.container?.classList.add( 'twc__property--slider-hidden' );

            for ( let i = 0; i < this.targets.length; i++ ) {
                const target = this.targets[ i ] as HTMLElement;

                if ( target.dataset.type !== targetType ) {
                    target.classList.remove( PropertyMediaHandler.classes.active );
                    continue;
                }

                const iframe = target.querySelector( 'iframe' ) as HTMLIFrameElement | null;

                if ( !iframe ) {
                    throw new Error( 'no <iframe> element found' );
                }

                if ( !iframe.hasAttribute( 'src' ) ) {
                    iframe.setAttribute( 'src', iframe.dataset.src || '' );
                }

                target.classList.add( PropertyMediaHandler.classes.active );
            }
        }

        for ( let j = 0; j < this.targets.length; j++ ) {
            const target = this.targets[ j ] as HTMLElement;
            const iframeToPause = target.querySelector( 'iframe' ) as HTMLIFrameElement | null;

            if ( !iframeToPause ) {
                throw new Error( 'no <iframe> element found' );
            }

            if ( iframeToPause.dataset.provider === 'youtube' && iframeToPause.hasAttribute( 'src' ) ) {
                iframeToPause.contentWindow?.postMessage( PropertyMediaHandler.postMessages[ 'youtube:pause' ], '*' );
                continue;
            }

            if ( iframeToPause.dataset.provider === 'vimeo' && iframeToPause.hasAttribute( 'src' ) ) {
                iframeToPause.contentWindow?.postMessage( PropertyMediaHandler.postMessages[ 'vimeo:pause' ], '*' );
                continue;
            }
        }

        this.active = targetType;
    }

    remove() {
        document.removeEventListener( 'twc:popup:open-property', this.boundUpdate );
        document.removeEventListener( 'twc:popup:close-property', this.boundOnPropertyClose );
    }

    getContainer() {
        return ( this.root as HTMLElement ).querySelector( '.twc__property--slider-primary' ) as HTMLElement | null;
    }

    getTargets() {
        return ( this.root as HTMLElement ).getElementsByClassName( PropertyMediaHandler.classes.target );
    }

    getBtns() {
        return ( this.root as HTMLElement ).getElementsByClassName( PropertyMediaHandler.classes.btn );
    }

    static classes = {
        btn: 'btn-media-launcher',
        target: 'twc__property--media',
        active: 'twc__property--media-active',
        initialised: 'twc__property--media-initialised',
    };

    static postMessages = {
        'youtube:pause': '{"event":"command","func":"pauseVideo","args":""}',
        'vimeo:pause': '{"method":"pause"}',
    };
}

export default function initPropertyMediaHandler( container: HTMLElement | Document ) {
    window.mediaHandler = new PropertyMediaHandler( container );
}
